import { AnimatePresence, motion } from "motion/react";
import { Fragment, useCallback, useState } from "react";
import {
  type Control,
  Controller,
  type UseFieldArrayReturn,
  useController,
  useFormState,
} from "react-hook-form";

import Checkbox from "@krea/shared/design-system/fields/Checkbox";
import { ControlledWrapper } from "@krea/shared/design-system/input/controlled-wrapper";
import { FieldError } from "@krea/shared/design-system/input/field";
import { Input } from "@krea/shared/design-system/input/input";
import {
  PercentOrMoneyInput,
  type PercentOrMoneyMode,
} from "@krea/shared/design-system/input/percent-or-money-input";
import { Label } from "@krea/shared/design-system/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@krea/shared/design-system/Select";
import type { Currency } from "@krea/shared/types/common";

import {
  FieldRequestFieldEnum,
  FieldRequestTypeEnum,
} from "@/features/common/enums";
import type { IFieldRequestInput } from "@/features/lender-responses/types/lender-response";
import { FIELD_REQUESTS_LABELS } from "@/utils/fieldRequests/fieldRequests";
import type { BidPostData } from "@/validations";

import DetailsItem from "../../../details/item";

const numberOfRequiredGuarantorsOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

interface IGuarantorCommitmentInputProps {
  control: Control<BidPostData>;
  fieldIndex: number;
  currency: Currency;
  disabled: boolean;
}

const GuarantorCommitmentInput = ({
  control,
  fieldIndex,
  currency,
  disabled,
}: IGuarantorCommitmentInputProps) => {
  const {
    field: { onChange: onChangeAmount, value: amountValue },
    fieldState: { error: amountError },
  } = useController({
    name: `fieldRequests.${fieldIndex}.config.guarantorCommitmentAmount`,
    control,
  });

  const {
    field: { onChange: onChangePercent, value: percentValue },
  } = useController({
    name: `fieldRequests.${fieldIndex}.config.reducedGuarantorPercentTo`,
    control,
  });

  const [mode, setMode] = useState<PercentOrMoneyMode>(
    amountValue != null ? "amount" : "percent",
  );

  const handleModeChange = (next: PercentOrMoneyMode) => {
    setMode(next);
    if (next === "percent") {
      onChangeAmount(null);
    } else {
      onChangePercent(null);
    }
  };

  const inputValue: number | null =
    mode === "amount" ? (amountValue ?? null) : (percentValue ?? 100);

  const handleChange = (n: number | null) => {
    if (mode === "amount") {
      onChangeAmount(n);
    } else if (n === null || n === 100) {
      onChangePercent(null);
    } else {
      onChangePercent(n);
    }
  };

  return (
    <div className="space-y-2">
      <PercentOrMoneyInput
        id={`guarantors-commitment-${fieldIndex}`}
        value={inputValue}
        onChange={handleChange}
        mode={mode}
        onModeChange={handleModeChange}
        currency={currency}
        disabled={disabled}
        aria-invalid={!!amountError}
      />
      {amountError && <FieldError errors={[amountError]} />}
    </div>
  );
};

interface FieldRequestSelectorProps {
  control: Control<BidPostData>;
  fieldRequests: IFieldRequestInput[];
  currency: Currency;
  // Shared across every FieldRequestSelector instance rendered for the same
  // form (see LenderResponseForm.tsx). Each instance calling its own
  // useFieldArray({ name: "fieldRequests" }) corrupts the shared array on
  // remove() (KREA-4311) since react-hook-form doesn't support multiple
  // independent useFieldArray hooks on the same field name.
  fieldRequestsArray: UseFieldArrayReturn<BidPostData, "fieldRequests">;
}

export const FieldRequestSelector = ({
  fieldRequests,
  control,
  currency,
  fieldRequestsArray,
}: FieldRequestSelectorProps) => {
  const { isSubmitting } = useFormState({ control });
  const { fields, append, remove } = fieldRequestsArray;

  const getFieldRequestIndex = useCallback(
    (fieldRequest: IFieldRequestInput) => {
      return fields.findIndex((item) => item.field === fieldRequest.field);
    },
    [fields],
  );

  return (
    <div className="space-y-4">
      <ul className="list-none">
        {fieldRequests.map((fieldRequest) => {
          const fieldIndex = getFieldRequestIndex(fieldRequest);
          const field = fieldIndex > -1 ? fields[fieldIndex] : null;

          return (
            <Fragment
              key={field ? field.id : `field-request-${fieldRequest.field}`}
            >
              <li className="mb-4">
                <Checkbox
                  id={`checkbox-${fieldRequest.field}`}
                  label={
                    FIELD_REQUESTS_LABELS[
                      fieldRequest.field as keyof typeof FIELD_REQUESTS_LABELS
                    ]
                  }
                  checked={!!field}
                  onChange={() => {
                    if (field) {
                      remove(fieldIndex);
                    } else {
                      append(fieldRequest);
                    }
                  }}
                  disabled={isSubmitting}
                />
              </li>

              <AnimatePresence>
                {field && (
                  <motion.div
                    initial={{
                      opacity: 0,
                      height: 0,
                    }}
                    animate={{
                      opacity: 1,
                      height: "auto",
                      transition: {
                        type: "tween",
                        duration: 0.1,
                        ease: "easeOut",
                      },
                    }}
                    exit={{
                      opacity: 0,
                      height: 0,
                      transition: {
                        type: "tween",
                        duration: 0.1,
                        ease: "easeOut",
                      },
                    }}
                  >
                    <DetailsItem className="mb-4 rounded-sm border border-gray-300 p-4">
                      <div className="w-full">
                        <ControlledWrapper
                          label="Comment to customer"
                          name={`fieldRequests.${fieldIndex}.lenderComment`}
                          control={control}
                          required={false}
                          render={({ field, fieldState }) => (
                            <Input
                              {...field}
                              value={field.value ?? ""}
                              id={`fieldRequests.${fieldIndex}.lenderComment`}
                              type="text"
                              disabled={isSubmitting}
                              data-invalid={fieldState.invalid}
                            />
                          )}
                        />
                      </div>

                      {field.field === FieldRequestFieldEnum.guarantors &&
                        field.config && (
                          <div className="w-full space-y-4">
                            <div className="space-y-2">
                              <Label
                                htmlFor={`number-of-guarantors-${fieldIndex}`}
                              >
                                Number of required guarantors
                                <span className="text-red-500"> *</span>
                              </Label>
                              <Controller
                                name={`fieldRequests.${fieldIndex}.config.numberOfRequiredGuarantors`}
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                  <Select
                                    value={String(value ?? 1)}
                                    onValueChange={(v) => onChange(Number(v))}
                                    disabled={isSubmitting}
                                  >
                                    <SelectTrigger
                                      id={`number-of-guarantors-${fieldIndex}`}
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {numberOfRequiredGuarantorsOptions.map(
                                        (n) => (
                                          <SelectItem key={n} value={String(n)}>
                                            {String(n)}
                                          </SelectItem>
                                        ),
                                      )}
                                    </SelectContent>
                                  </Select>
                                )}
                              />
                            </div>

                            <div className="space-y-2">
                              <Label
                                htmlFor={`guarantors-commitment-${fieldIndex}`}
                              >
                                Guarantors commitment
                              </Label>
                              <GuarantorCommitmentInput
                                control={control}
                                fieldIndex={fieldIndex}
                                currency={currency}
                                disabled={isSubmitting}
                              />
                            </div>
                          </div>
                        )}

                      {(field.type !== FieldRequestTypeEnum.COLLATERAL ||
                        field.field === FieldRequestFieldEnum.guarantors) && (
                        <Controller
                          name={`fieldRequests.${fieldIndex}.isCollectedByLender`}
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <Checkbox
                              className="mt-1.5"
                              id={`is_collected_by_lender-${fieldRequest.field}`}
                              label="Information provided directly to lender platform"
                              checked={value}
                              onChange={() => onChange(!value)}
                              disabled={isSubmitting}
                            />
                          )}
                        />
                      )}
                    </DetailsItem>
                  </motion.div>
                )}
              </AnimatePresence>
            </Fragment>
          );
        })}
      </ul>
    </div>
  );
};

export default FieldRequestSelector;
